import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseUrl } from "../../helpers/constants";
import { getCookie, signout } from "../../helpers/cookies";

const customFetchBaseQuery = fetchBaseQuery({
    baseUrl: baseUrl,
    prepareHeaders: (headers, { getState }) => {
        const token = getCookie("magiclock_admin_token");
        if (token) {
            headers.set("Authorization", `BEARER ${token}`);
        }
        return headers;
    },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
    let result = await customFetchBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 403) {
        signout(() => {
            window.location.href = "/login";
        });
    }

    return result;
};

// Insurance parser function
const insuranceParser = (response) => {
    try {
        // Follow the same pattern as other parsers - check for .result first
        if (response?.result) {
            response = response.result;
        }

        if (!response) {
            return {
                results: [],
                count: 0,
                pagination: {
                    page: 1,
                    limit: 10,
                    totalPages: 1,
                },
            };
        }

        // Handle the direct format from your API response
        if (response.insurance_renewals) {
            response.results = response.insurance_renewals.map((row) => {
                return {
                    id: row.id || "",
                    customer_name: row.customer_name || "",
                    email: row.email || "",
                    mobile: row.mobile || "",
                    address: row.address || "",
                    city: row.city || "",
                    state: row.state || "",
                    pincode: row.pincode || "",
                    policy_number: row.policy_number || "",
                    expiry_date: row.expiry_date || "",
                    product_id: row.product_id || "",
                    product_name: row.product_name || "",
                    insurance_number: row.insurance_number || "",
                    validity_from: row.validity_from || "",
                    validity_to: row.validity_to || "",
                    premium_amount: row.premium_amount || "",
                    files: row.files || "",
                    notes: row.notes || "",
                    payment_status: row.payment_status || "",
                    paid_amount: row.paid_amount || "",
                    transaction_id: row.transaction_id || "",
                    payment_method: row.payment_method || "",
                    created_at: row.created_at || "",
                    updated_at: row.updated_at || "",
                    product: {
                        id: row.product?.id || "",
                        name: row.product?.name || "",
                        description: row.product?.description || "",
                        image_url: row.product?.image_url || "",
                    },
                };
            });
            response.count = response.pagination?.total || 0;
        }

        return response;
    } catch (error) {
        throw new Error(error);
    }
};

const insuranceApis = createApi({
    reducerPath: "insuranceApis",
    baseQuery: baseQueryWithReauth,
    keepUnusedDataFor: 0,
    tagTypes: ["InsuranceRenewals", "InsuranceDetails"],
    endpoints: (build) => ({
        // Admin insurance renewals
        getAdminInsuranceRenewals: build.query({
            query: (params) => {
                // Remove unsupported parameters
                const { sort_by, sort_order, search, ...validParams } = params;
                return {
                    url: "/common/insurance-renewals",
                    method: "GET",
                    params: validParams,
                };
            },
            transformResponse: (response) => insuranceParser(response),
            providesTags: ["InsuranceRenewals"],
            keepUnusedDataFor: 5,
        }),
        getAdminInsuranceDetails: build.query({
            query: (id) => ({
                url: `/common/insurance-renewal/${id}`,
                method: "GET",
            }),
            transformResponse: (response) => {
                // Follow the same pattern as other APIs - check for .result first
                if (response?.result) {
                    return response.result;
                }
                return response;
            },
            providesTags: ["InsuranceDetails"],
            keepUnusedDataFor: 5,
        }),
        // Delete insurance renewal
        deleteInsuranceRenewal: build.mutation({
            query: (id) => ({
                url: `/common/insurance-renewal/${id}`,
                method: "DELETE",
            }),
            invalidatesTags: ["InsuranceRenewals"],
        }),
    }),
});

export const {
    useGetAdminInsuranceRenewalsQuery,
    useGetAdminInsuranceDetailsQuery,
    useDeleteInsuranceRenewalMutation,
} = insuranceApis;
export default insuranceApis;
